import { MarketingChannel } from "@redotech/redo-billing-common/marketing-channel";
import {
  baseMarketingSubscriptionToConsentStatus,
  Customer,
  MarketingConsentStatus,
} from "@redotech/redo-model/customer";
import { MarketingTeam } from "@redotech/redo-model/marketing/marketing-team-zod";
import { safeAssertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const AvailableMarketingChannelSchema_ = z.object({
  channel: createEnum(MarketingChannel),
  channelId: z.string(),
});
export const AvailableMarketingChannelSchema: z.ZodType<AvailableMarketingChannel> =
  AvailableMarketingChannelSchema_;
export interface AvailableMarketingChannel extends z.infer<
  typeof AvailableMarketingChannelSchema_
> {}

/**
 * Checks if a customer can receive email marketing based on consent status.
 * Email: Can receive if NOT unsubscribed (CONFIRMED, SUBSCRIBED, or NOT_SUBSCRIBED)
 */
export function canReceiveEmailMarketing(
  status: MarketingConsentStatus,
): boolean {
  return status !== MarketingConsentStatus.UNSUBSCRIBED;
}

/**
 * Checks if a customer can receive SMS marketing based on consent status.
 * SMS: Can receive ONLY if CONFIRMED
 */
export function canReceiveSmsMarketing(
  status: MarketingConsentStatus,
): boolean {
  return status === MarketingConsentStatus.CONFIRMED;
}

/**
 * Gets the validated channel ID if the customer can receive marketing on that channel.
 * Returns the channelId if valid, undefined if not subscribed or not found.
 */
export function getCustomerChannelId(
  customer: Pick<Customer, "emails" | "phoneNumbers">,
  channel: MarketingChannel,
  channelId: string,
): string | undefined {
  if (channel === MarketingChannel.EMAIL) {
    const email = customer.emails?.find(
      (email) =>
        email.email === channelId &&
        canReceiveEmailMarketing(
          baseMarketingSubscriptionToConsentStatus(email.marketingSubscription),
        ),
    );
    return email?.email;
  } else if (channel === MarketingChannel.SMS) {
    const phoneNumber = customer.phoneNumbers?.find(
      (phoneNumber) =>
        phoneNumber.phoneNumber === channelId &&
        canReceiveSmsMarketing(
          baseMarketingSubscriptionToConsentStatus(
            phoneNumber.marketingSubscription,
          ),
        ),
    );
    return phoneNumber?.phoneNumber;
  }
  safeAssertNever(channel);
}

/**
 * Determines which marketing channels are available for a customer based on
 * contact info and subscription status.
 *
 * Email: Can receive if NOT unsubscribed (CONFIRMED, SUBSCRIBED, or NOT_SUBSCRIBED)
 * SMS: Can receive ONLY if CONFIRMED
 */
export function getAvailableMarketingChannels(
  customer: Pick<Customer, "emails" | "phoneNumbers"> | null | undefined,
): AvailableMarketingChannel[] {
  if (!customer) {
    return [];
  }

  const availableChannels: AvailableMarketingChannel[] = [];

  // Email: can receive if NOT unsubscribed
  const emailWithSubscription = customer.emails?.find((email) => {
    const status = baseMarketingSubscriptionToConsentStatus(
      email.marketingSubscription,
    );
    return email.email && canReceiveEmailMarketing(status);
  });

  if (emailWithSubscription?.email) {
    availableChannels.push({
      channel: MarketingChannel.EMAIL,
      channelId: emailWithSubscription.email,
    });
  }

  // SMS: can receive ONLY if CONFIRMED
  const phoneWithSubscription = customer.phoneNumbers?.find((phone) => {
    const status = baseMarketingSubscriptionToConsentStatus(
      phone.marketingSubscription,
    );
    return phone.phoneNumber && canReceiveSmsMarketing(status);
  });

  if (phoneWithSubscription?.phoneNumber) {
    availableChannels.push({
      channel: MarketingChannel.SMS,
      channelId: phoneWithSubscription.phoneNumber,
    });
  }

  return availableChannels;
}

/**
 * Checks if a team has marketing enabled for a specific channel.
 * Requires both overall marketing to be enabled and channel-specific billing to be enabled.
 */
export function isTeamEnabledForMarketingChannel(
  team: Pick<MarketingTeam, "settings">,
  channel: MarketingChannel,
): boolean {
  const marketingSettings = team.settings.marketing;
  if (!marketingSettings?.enabled) {
    return false;
  }
  if (channel === MarketingChannel.EMAIL) {
    return marketingSettings.emailBilling?.enabled || false;
  } else if (channel === MarketingChannel.SMS) {
    return marketingSettings.smsBilling?.enabled || false;
  }
  safeAssertNever(channel);
}

interface TextConciergeSmsMarketingChannel {
  channel: MarketingChannel.SMS;
  channelId: string;
  useRecover: boolean;
}
export function getTextConciergeSmsMarketingChannel(
  customer: Pick<
    Customer,
    "emails" | "phoneNumbers" | "supportCommunicationConsent"
  >,
  team: Pick<MarketingTeam, "settings">,
  targetCustomerPhoneNumber?: string,
): TextConciergeSmsMarketingChannel | undefined {
  const isTextConciergeEnabled = !!team.settings.shopperAgent?.enabled;
  if (!isTextConciergeEnabled) {
    return undefined;
  }

  // Concierge is technically using the Support system so we need to check for support opt-out.
  if (customer.supportCommunicationConsent?.textMessages?.optOutDate) {
    return undefined;
  }

  let phoneNumbers = customer.phoneNumbers ?? [];
  if (targetCustomerPhoneNumber) {
    phoneNumbers = phoneNumbers.filter(
      (phone) => phone.phoneNumber === targetCustomerPhoneNumber,
    );
  }

  let preferredChannel: TextConciergeSmsMarketingChannel | undefined;
  for (const phone of phoneNumbers) {
    const phoneNumber = phone.phoneNumber;
    if (!phoneNumber) {
      continue;
    }

    const status = baseMarketingSubscriptionToConsentStatus(
      phone.marketingSubscription,
    );

    // Ideal case, use the confirmed SMS subscription rather than Recover
    if (canReceiveSmsMarketing(status)) {
      preferredChannel = {
        channel: MarketingChannel.SMS,
        channelId: phoneNumber,
        useRecover: false,
      };
      break;
    }

    // An explicit SMS-marketing opt-out blocks the Recover fallback too. Mirror
    // the gate in evaluateRecoverEligibility here so we don't select a number
    // that the gate will immediately reject.
    if (status === MarketingConsentStatus.UNSUBSCRIBED) {
      continue;
    }

    if (!phone.supportCommunicationConsent?.textMessages?.optOutDate) {
      // Fall back to Recover. Keep iterating in case a later phone has a confirmed
      // marketing subscription.
      preferredChannel ??= {
        channel: MarketingChannel.SMS,
        channelId: phoneNumber,
        useRecover: true,
      };
    }
  }

  return preferredChannel;
}
